import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import * as XLSX from 'xlsx';
import { SpreadsheetViewers } from '../index';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConstantsReview from '../ConstantsReview';
import { WorkflowConstants } from '../../types/WorkflowConstants';
import type { RebateAgreementListItem } from 'src/api/rebates/types';

// Phase 0: Data Review - Handles both sheet selection and image review
// All other phases (1-3) work with data from the selected sheet or parsed image
interface PhaseZeroSheetSelectionProps {
  workbook: XLSX.WorkBook | null;
  selectedSheet: string | null;
  originalData: unknown[][];
  isLoadingOriginal: boolean;
  selectedFiles: File[];
  onSheetChange: (sheetName: string) => void;
  onOriginalDataChange: (data: unknown[][]) => void;
  // File type for dynamic step naming
  fileType?: 'csv' | 'xlsx' | 'image' | null;
  // New props for image review mode
  isImageReviewMode?: boolean;
  onImageReparse?: (customInstructions: string) => Promise<void>;
  isReparsing?: boolean;
  // Constants panel props
  constants?: WorkflowConstants | null;
  onConstantsChange?: (next: WorkflowConstants | null) => void;
  detectingConstants?: boolean;
  // Visual cue controlled by parent business logic
  showNoProductGroupsWarning?: boolean;
  showNoFinalizedAgreementsWarning?: boolean;
  rebateAgreementWarningMessage?: string;
  // Supplier agreements for display
  supplierAgreements?: RebateAgreementListItem[];
}

const PhaseZeroSheetSelection: React.FC<PhaseZeroSheetSelectionProps> = ({
  workbook,
  selectedSheet,
  originalData,
  isLoadingOriginal,
  selectedFiles,
  onSheetChange,
  onOriginalDataChange,
  fileType = null,
  isImageReviewMode = false,
  onImageReparse: _onImageReparse,
  isReparsing = false,
  constants,
  onConstantsChange,
  detectingConstants = false,
  showNoFinalizedAgreementsWarning = false,
  rebateAgreementWarningMessage = '',
  supplierAgreements = [],
}) => {
  // (Auto-parse logic moved to parent to avoid duplicate triggers)
  // const shouldShowDataPreview =
  //   (!!selectedSheet && !!workbook) || isImageReviewMode;

  const shouldShowDataPreview = !!originalData && originalData.length > 0;

  const showSheetSelection =
    !isImageReviewMode && !!workbook && workbook.SheetNames.length > 1;

  const getStepTitle = () => {
    switch (fileType) {
      case 'xlsx':
        return 'Select Sheet and Extract Constants';
      case 'csv':
        return 'Extract Constants';
      case 'image':
        return 'Parse Image and Extract Constants';
      default:
        return isImageReviewMode ? 'Review Parsed Image Data' : 'Select Sheet';
    }
  };

  const getStepDescription = () => {
    switch (fileType) {
      case 'xlsx':
        return 'First, choose which sheet to process from your Excel file.';
      case 'csv':
        return 'Review the data extracted from your CSV file and configure constants like supplier, currency, date range, etc.';
      case 'image':
        return 'Review the data extracted from your image(s). You can use the AI Assistant to re-parse with custom instructions if needed.';
      default:
        return isImageReviewMode
          ? 'Review the data extracted from your image(s). You can use the AI Assistant to re-parse with custom instructions if needed.'
          : 'First, choose which sheet to process from your Excel file.';
    }
  };

  const stepDescription =
    'The goal of this step is to extract the data from your source file and figure out which constants like supplier, currency, date range, etc. are present.';

  return (
    <div>
      {/* Conditional Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">{getStepTitle()}</h2>
        <p className="text-sm text-gray-600">{stepDescription}</p>
        <p className="text-sm text-gray-600">{getStepDescription()}</p>
      </div>

      {/* Constants Panel */}
      {constants && onConstantsChange && (
        <div className="mb-6">
          <Card>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Detected Constants</h3>
              </div>
              {detectingConstants && (
                <div className="mb-4">
                  <LoadingSpinner
                    text="Extracting constants..."
                    minHeight={80}
                    size={40}
                  />
                </div>
              )}
              <ConstantsReview
                constants={constants}
                onConstantsChange={onConstantsChange}
                disabled={false}
              />
              {/* Warning Message */}
              {showNoFinalizedAgreementsWarning && (
                <div className="mt-4">
                  <Badge variant="destructive">
                    {rebateAgreementWarningMessage ||
                      'Selected supplier has no finalized rebate agreements'}
                  </Badge>
                </div>
              )}

              {/* Supplier Agreements Table */}
              {supplierAgreements.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2">
                    Applicable Rebate Agreements ({supplierAgreements.length})
                  </h4>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Agreement Name</TableHead>
                          <TableHead>Date Range</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {supplierAgreements.map((agreement) => {
                          const isExpired =
                            agreement.end_date <
                            new Date().toISOString().split('T')[0];
                          return (
                            <TableRow key={agreement.id}>
                              <TableCell className="font-medium">
                                {agreement.name}
                              </TableCell>
                              <TableCell>
                                <span
                                  className={isExpired ? 'text-gray-500' : ''}
                                >
                                  {agreement.start_date} to {agreement.end_date}
                                </span>
                                {isExpired && (
                                  <Badge
                                    variant="outline"
                                    className="ml-2 text-xs"
                                  >
                                    Expired
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    agreement.status === 'finalized'
                                      ? 'default'
                                      : 'secondary'
                                  }
                                >
                                  {agreement.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <a
                                  href={`/rebates/agreements/${agreement.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                  title="Open agreement in new tab"
                                >
                                  <ArrowSquareOutIcon className="h-4 w-4" />
                                </a>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    CSV line items must fall within a finalized agreement's date
                    range. Expired agreements are valid for historical data.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sheet Selection Component - Only show for XLSX files */}
      {showSheetSelection && (
        <div className="mb-4">
          {/* Sheet Selection Tabs */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <Tabs
              value={selectedSheet || workbook.SheetNames[0]}
              onValueChange={onSheetChange}
            >
              <TabsList className="inline-flex h-10 items-center justify-start rounded-lg bg-muted p-1 text-muted-foreground gap-2">
                {workbook.SheetNames.map((sheetName: string) => (
                  <TabsTrigger
                    key={sheetName}
                    value={sheetName}
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow"
                  >
                    <span className="truncate max-w-[160px]">{sheetName}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>
      )}

      {/* Data Preview - Show for both modes */}
      <div className={showSheetSelection ? '-mt-4' : ''}>
        {/* Debug: PhaseZero originalData */}
        {shouldShowDataPreview ? (
          <div className="relative">
            {/* OCR Re-parsing Overlay */}
            {isReparsing && isImageReviewMode && (
              <div className="absolute inset-0 bg-white bg-opacity-95 z-10 flex items-center justify-center rounded-lg">
                <LoadingSpinner
                  text="Re-parsing images with AI OCR..."
                  minHeight={200}
                  size={80}
                />
              </div>
            )}
            <SpreadsheetViewers
              title={isImageReviewMode ? 'Parsed Image Data' : 'Sheet Preview'}
              originalTitle={
                isImageReviewMode ? 'Parsed Image Data' : 'Original Data'
              }
              transformedTitle={isImageReviewMode ? '—' : 'Transformed Data'}
              originalData={originalData}
              transformedData={[]} // No transformed data in Phase 0
              isLoadingOriginal={isLoadingOriginal || isReparsing}
              isLoadingTransformed={false}
              transformError={null}
              initialViewMode="single-view" // Use single-view mode
              workbook={null} // Disable sheet switching in preview
              selectedSheet={selectedSheet || undefined}
              onSheetChange={() => {}} // Disable sheet switching in preview
              onConversationClear={() => {}}
              onOriginalDataChange={onOriginalDataChange}
              filename={selectedFiles[0]?.name || 'spreadsheet'}
            />
          </div>
        ) : (
          <div className="h-96 border border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
            {/* Debug: PhaseZero no data */}
            <LoadingSpinner
              text={
                isLoadingOriginal || isReparsing
                  ? isImageReviewMode
                    ? 'Parsing image data with AI OCR...'
                    : 'Loading sheet data...'
                  : isImageReviewMode
                    ? 'No data parsed from images'
                    : 'No data available for this sheet'
              }
              minHeight={300}
              size={80}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PhaseZeroSheetSelection;
